// =============================================
// Types för Briefs-modulen
// =============================================

export interface AdPlacementTemplate {
  _id: string;
  type: 'print' | 'digital';
  displayName: string;
  publisherName?: string;
  channelName?: string;
  formatName: string;
  dimensions?: {
    width: number;
    height: number;
    unit: 'mm' | 'px';
  };
  bleed?: number;
  safeArea?: number;
  colorProfile?: string;
  fileFormat?: string;
  maxFileSize?: string;
  resolution?: number;
  deadlineInfo?: string;
  contactEmail?: string;
  contactPhone?: string;
  previewImageUrl?: string;
  order: number;
  isActive: boolean;
}

export interface BriefProductSnapshot {
  title: string;              // Produktnamn (EJ filnamn)
  artikelnummer?: string;
  gtin?: string;
  netContent?: string;
  description?: string;       // Produktbeskrivning
  ingredienser?: string;
  ursprung?: string;
  forpackningsstorlek?: string;
  previewUrl?: string;
}

export interface BriefProductPrice {
  amount: number;
  currency: string;
  type: 'campaign' | 'regular';
  originalPrice?: number;
  validFrom?: string;
  validTo?: string;
}

export interface BriefProduct {
  assetId: string;
  snapshot: BriefProductSnapshot;
  role: 'hero' | 'support';
  price?: BriefProductPrice;
  chainNumber?: string;  // Kedjenummer per produkt
}

export interface BriefAttachment {
  _id?: string;
  fileName: string;
  mimeType: string;
  size: number;
  path: string;
  uploadedAt: string;
}

export interface BriefAdCopyVariant {
  headline: string;
  subhead?: string;
  body: string;
  cta?: string;
  priceText?: string;
}

export interface BriefAiOutput {
  briefText: string;
  strategyText?: string;            // Användarens aktuella/redigerade strategitext
  strategySuggestionText?: string;  // Senaste AI-förslaget för strategi
  includeStrategyInClickup?: boolean; // Om strategi ska med i ClickUp (default true)
  adCopyVariants?: BriefAdCopyVariant[];
  missingInfoQuestions?: string[];
  model: string;
  promptVersion: string;
  generatedAt: string;
}

export interface BriefAudienceSelection {
  id: string;
  label: string;
}

export interface Brief {
  _id: string;
  title: string;
  type: 'print' | 'digital';
  status: 'draft' | 'generated' | 'submitted' | 'approved';
  
  placementTemplateId?: string;
  placementSnapshot?: {
    displayName: string;
    publisherName?: string;
    channelName?: string;
    formatName: string;
    dimensions?: { width: number; height: number; unit: string };
    deadlineInfo?: string;
  };
  
  products: BriefProduct[];
  hasPrices: boolean;
  includeEan?: boolean;
  includeChainNumber?: boolean;
  chainNumber?: string;
  
  primaryAudience?: BriefAudienceSelection;
  secondaryAudiences?: BriefAudienceSelection[];
  
  objectivePrimary?: string;
  objectiveSecondary?: string;
  campaignSeason?: string;
  
  technicalRequirements?: string;
  otherNotes?: string;
  deadline?: string;
  deadlineText?: string;
  
  attachments: BriefAttachment[];
  
  aiOutput?: BriefAiOutput;
  
  // ClickUp-integration
  clickupTaskId?: string;
  clickupTaskUrl?: string;
  clickupSentAt?: string;
  
  createdBy: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Produkt-sökresultat
export interface ProductSearchResult {
  assetId: string;
  title: string;
  artikelnummer?: string;
  gtin?: string;
  netContent?: string;
  previewUrl?: string;
  description?: string;
}

// Wizard-steg
export type WizardStep = 
  | 'type'           // Steg 1: Print eller Digital
  | 'template'       // Steg 2: Välj annonsmall
  | 'products'       // Steg 3: Välj produkter + priser
  | 'audience'       // Steg 4: Målgrupp
  | 'objective'      // Steg 5: Syfte
  | 'technical'      // Steg 6: Teknisk info + bilagor
  | 'review';        // Steg 7: Granska & generera

export const WIZARD_STEPS: { step: WizardStep; label: string }[] = [
  { step: 'type', label: 'Annonstyp' },
  { step: 'template', label: 'Annonsmall' },
  { step: 'products', label: 'Produkter' },
  { step: 'audience', label: 'Målgrupp' },
  { step: 'objective', label: 'Syfte' },
  { step: 'technical', label: 'Teknisk info' },
  { step: 'review', label: 'Granska' },
];
